import { useState, useEffect } from "react";
import {
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
} from "reactstrap";
import { AddressCardComponent } from "./addressCard/addressCard.component";
import { axiosInstance } from "../../api/axiosInstance";
import { ComponentLoader } from "../loaders";
import { activateNotification } from "../../utils/notifications-utils";
import { AddUserAddressForm } from "./forms/addUserAddressForm";
import { CustomModal } from "./addAddress/addAddressModal.style";
import { useApi } from "../../hooks/api.hook";
import { isAxiosError } from "axios";
import { CloseGrayedIcon } from "../../assets/icons/close";

export const AddressManagementModalComponent = ({
  isOpen,
  view,
  toggle,
  onSelect,
  onAddressAdded,
}) => {
  const [internalView, setInternalView] = useState(view || "list");
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [responseErrors, setResponseErrors] = useState([]);
  const api = useApi();

  const fetchAddresses = async () => {
    setLoading(true);
    try {
      const { data } = await axiosInstance.get("/address");
      setAddresses(data.addresses || []);
    } catch (err) {
      activateNotification({
        title: "Error",
        body: "Failed to fetch addresses",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleDelete = async (id) => {
    try {
      await axiosInstance.delete(`/address/${id}`);
      setAddresses((prev) => prev.filter((a) => a._id !== id));
      activateNotification({ title: "Deleted", body: "Address removed" });
    } catch {
      activateNotification({
        title: "Error",
        body: "Failed to delete address",
      });
    }
  };
  const addAddress = async (data) => {
    setLoading(true);
    try {
      const { data: respData } = await api.post("/address", data);
      setAddresses((prevState) => [...prevState, respData.address]);
      setInternalView("list");
      activateNotification({ title: "Success", body: "Information updated" });
      onAddressAdded?.();
      toggle();
    } catch (e) {
      let errorMessage = "Failed to update info";
      if (isAxiosError(e)) {
        const data = e.response?.data;

        if (data?.errors && typeof data.errors === "object") {
          setResponseErrors(data.errors);
        } else {
          errorMessage = data?.message || e.message || errorMessage;
        }
      } else {
        errorMessage = e.message || errorMessage;
      }
      activateNotification({ title: "Error", body: errorMessage });
    } finally {
      setLoading(false);
    }
  };
  const editAddress = async (data) => {
    setLoading(true);
    try {
      const { data: respData } = await api.post("/address", data);
      setAddresses((prevState) => [...prevState, respData.address]);
      setInternalView("list");
      activateNotification({ title: "Success", body: "Information updated" });
      toggle();
    } catch (e) {
      let errorMessage = "Failed to update info";
      if (isAxiosError(e)) {
        const data = e.response?.data;

        if (data?.errors && typeof data.errors === "object") {
          setResponseErrors(data.errors);
        } else {
          errorMessage = data?.message || e.message || errorMessage;
        }
      } else {
        errorMessage = e.message || errorMessage;
      }
      activateNotification({ title: "Error", body: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setInternalView("list");
      setSelectedAddress(null);
      setResponseErrors([]);
    }
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) {
      if (internalView === "list") fetchAddresses();
      else setLoading(false);
    }
  }, [isOpen, internalView]);
  useEffect(() => {
    if (view) setInternalView(view);
  }, [view]);

  return (
    <CustomModal fullscreen={"md"} isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>
        <div className="close-btn" onClick={toggle}>
          <CloseGrayedIcon />
        </div>
        <span>
          {internalView === "list" && "Select Delivery Address"}
          {internalView === "add" && "Add Delivery Address"}
          {internalView === "edit" && "Edit Delivery Address"}
        </span>
      </ModalHeader>
      <hr className="custom-divider" />
      <ModalBody>
        {loading ? (
          <ComponentLoader />
        ) : internalView === "list" ? (
          <Row>
            {addresses.length === 0 ? (
              <p>No address found</p>
            ) : (
              addresses.map((address) => (
                <Col md={12} key={address._id} className="mb-2">
                  <AddressCardComponent
                    active={selectedAddress?._id === address._id}
                    includeDelete={false}
                    address={address}
                    onDelete={handleDelete}
                    onEdit={() => {
                      setSelectedAddress(address);
                      setInternalView("edit");
                    }}
                    onSelect={(addr) => {
                      setSelectedAddress(addr);
                      onSelect?.(addr);
                    }}
                  />
                </Col>
              ))
            )}
          </Row>
        ) : (
          <AddUserAddressForm
            onSubmit={internalView === "edit" ? editAddress : addAddress}
            loading={loading}
            responseErrors={responseErrors}
          />
        )}
      </ModalBody>

      {internalView === "list" && (
        <ModalFooter>
          <Button color="success" onClick={() => setInternalView("add")}>
            Add New Address
          </Button>
        </ModalFooter>
      )}
    </CustomModal>
  );
};
export const useAddress = () => {
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const toggleAddressModal = () => setIsAddressModalOpen((prev) => !prev);

  return { isAddressModalOpen, setIsAddressModalOpen, toggleAddressModal };
};
